import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Tabs,
  Tab,
  Typography,
  Grid,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    maxWidth: "900px",
    width: "100%",
    maxHeight: "90vh",
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(2, 3),
  backgroundColor: theme.palette.mode === "light" ? "#f5f5f5" : "#1e2836",
}));

const TabPanel = ({ children, value, index }) => {
  return (
    <div hidden={value !== index} style={{ paddingTop: "24px" }}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const InfoRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  padding: theme.spacing(2, 1),
  borderBottom: `1px solid ${
    theme.palette.mode === "light" ? "#e0e0e0" : "rgba(255,255,255,0.1)"
  }`,
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.secondary,
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  textAlign: "right",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.1rem",
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(3),
  color: theme.palette.primary.main,
}));

const HighlightedPaper = styled(Paper)(({ theme, bgcolor }) => ({
  padding: theme.spacing(3),
  backgroundColor: bgcolor || (theme.palette.mode === "light" ? "#FFF8E1" : "#3a3a2a"),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(1),
}));

const WarningBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.mode === "light" ? "#FFEBEE" : "#5d2a2a",
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.mode === "light" ? "#ef5350" : "#ef9a9a"}`,
  marginTop: theme.spacing(2),
}));

const WarningChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "light" ? "#FFA726" : "#d84315",
  color: "#fff",
  fontWeight: 600,
  marginTop: theme.spacing(0.5),
}));

const BillDetailsDialog = ({ open, onClose, bill }) => {
  const [activeTab, setActiveTab] = useState(0);

  if (!bill) return null;

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "n/d";
    return `€ ${parseFloat(value).toFixed(2)}`;
  };

  const formatDate = (date) => {
    if (!date) return "n/d";
    return date;
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return "n/d";
    return value;
  };

  const getServiceIcon = (tipo) => {
    switch (tipo?.toLowerCase()) {
      case "luce":
        return <ElectricBoltIcon />;
      case "gas":
        return <LocalGasStationIcon />;
      default:
        return <ElectricBoltIcon />;
    }
  };

  // Calcolo dei campi inceriti mancanti
  const campiIncertiList = [];
  const metadata = bill._billMetadata || {};
  
  if (!metadata.numeroContratto) campiIncertiList.push("dati_contratto.numero_contratto");
  if (!metadata.dataAttivazione) campiIncertiList.push("dati_contratto.data_attivazione");
  if (bill.tipoServizio?.toLowerCase() === "luce" && !metadata.potenzaDisponibile) {
    campiIncertiList.push("consumi.luce.potenza_disponibile_kW");
  }

  return (
    <StyledDialog open={open} onClose={onClose}>
      <StyledDialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {getServiceIcon(bill.tipoServizio)}
          <Typography variant="h6" fontWeight={700}>
            {bill.fornitore || "Rubino Energas"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <DialogContent sx={{ px: 4, py: 3 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
          File: {bill.fileName || "E2601363_13_01_2026_RUB0004952_RANIERI_GIUSEPPE.pdf"}
        </Typography>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Dettagli Bolletta" sx={{ flex: 1 }} />
          <Tab label="Analisi & Comparazione Mercato" sx={{ flex: 1 }} />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={4}>
            {/* DATI CHIAVE */}
            <Grid item xs={12} md={6}>
              <SectionTitle>DATI CHIAVE</SectionTitle>
              <InfoRow>
                <InfoLabel>Tipo Fornitura</InfoLabel>
                <InfoValue>
                  <Chip
                    label={bill.tipoServizio?.toUpperCase() || "LUCE"}
                    color="primary"
                    size="small"
                  />
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Totale Bolletta</InfoLabel>
                <InfoValue>{formatCurrency(bill.totaleBolletta)}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Periodo</InfoLabel>
                <InfoValue>{formatDate(bill.dataInizio)} - {formatDate(bill.dataFine)}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Giorni</InfoLabel>
                <InfoValue>{formatValue(bill.giorni)}</InfoValue>
              </InfoRow>
            </Grid>

            {/* CONSUMI */}
            <Grid item xs={12} md={6}>
              <SectionTitle>CONSUMI</SectionTitle>
              {bill.tipoServizio?.toLowerCase() === "luce" && (
                <>
                  <InfoRow>
                    <InfoLabel>Totale kWh</InfoLabel>
                    <InfoValue>{formatValue(metadata?.totaleKwh)}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>F1</InfoLabel>
                    <InfoValue>{formatValue(metadata?.f1)}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>F2</InfoLabel>
                    <InfoValue>{formatValue(metadata?.f2)}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>F3</InfoLabel>
                    <InfoValue>{formatValue(metadata?.f3)}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Potenza Imp. (kW)</InfoLabel>
                    <InfoValue>{formatValue(metadata?.potenzaImpegnata)}</InfoValue>
                  </InfoRow>
                </>
              )}
            </Grid>

            {/* INTESTATARIO */}
            <Grid item xs={12} md={6}>
              <SectionTitle>INTESTATARIO</SectionTitle>
              <InfoRow>
                <InfoLabel>Nominativo</InfoLabel>
                <InfoValue>
                  {bill.nomeIntestatario || bill.nome} {bill.cognomeIntestatario || bill.cognome}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Ragione Sociale</InfoLabel>
                <InfoValue>{formatValue(metadata?.ragioneSociale)}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>CF / P.IVA</InfoLabel>
                <InfoValue>{formatValue(bill.codiceFiscale || metadata?.cf)}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Città Fornitura</InfoLabel>
                <InfoValue>{formatValue(metadata?.cittaFornitura)}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Indirizzo</InfoLabel>
                <InfoValue>{formatValue(bill.indirizzo || metadata?.indirizzo)}</InfoValue>
              </InfoRow>
            </Grid>

            {/* ANALISI PREZZI */}
            <Grid item xs={12} md={6}>
              <SectionTitle>ANALISI PREZZI</SectionTitle>
              <HighlightedPaper 
                sx={{ 
                  backgroundColor: (theme) => theme.palette.mode === "light" ? "#E3F2FD" : "#1a3a4a"
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Materia Prima
                </Typography>
                <InfoRow sx={{ borderBottom: "none", pt: 0 }}>
                  <InfoLabel>Costo Energia (€/kWh)</InfoLabel>
                  <InfoValue>{formatValue(metadata?.costoEnergia)}</InfoValue>
                </InfoRow>
                <InfoRow sx={{ borderBottom: "none" }}>
                  <InfoLabel>Costo Gas (€/Smc)</InfoLabel>
                  <InfoValue>{formatValue(metadata?.costoGas)}</InfoValue>
                </InfoRow>
                <InfoRow sx={{ borderBottom: "none" }}>
                  <InfoLabel>Totale Materia Prima €</InfoLabel>
                  <InfoValue fontWeight={700}>{formatValue(metadata?.totaleMateriaPrima)}</InfoValue>
                </InfoRow>
              </HighlightedPaper>

              <HighlightedPaper 
                sx={{ 
                  backgroundColor: (theme) => theme.palette.mode === "light" ? "#F3E5F5" : "#3a1a3a"
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Costi Fissi (PCV)
                </Typography>
                <InfoRow sx={{ borderBottom: "none", pt: 0 }}>
                  <InfoLabel>PCV Mensile</InfoLabel>
                  <InfoValue>{formatValue(metadata?.pcvMensile)}</InfoValue>
                </InfoRow>
                <InfoRow sx={{ borderBottom: "none" }}>
                  <InfoLabel>Totale Spesa Fissa €</InfoLabel>
                  <InfoValue fontWeight={700}>{formatValue(metadata?.totaleSpesaFissa)}</InfoValue>
                </InfoRow>
              </HighlightedPaper>
            </Grid>

            {/* FORNITURA (POD/PDR) */}
            <Grid item xs={12} md={6}>
              <SectionTitle>FORNITURA (POD/PDR)</SectionTitle>
              {bill.tipoServizio?.toLowerCase() === "luce" && (
                <InfoRow>
                  <InfoLabel>POD (Luce)</InfoLabel>
                  <InfoValue>{formatValue(metadata?.pod)}</InfoValue>
                </InfoRow>
              )}
              {(bill.tipoServizio?.toLowerCase() === "gas" || 
                bill.tipoServizio?.toLowerCase() === "luce-gas") && (
                <InfoRow>
                  <InfoLabel>PDR (Gas)</InfoLabel>
                  <InfoValue>{formatValue(metadata?.pdr)}</InfoValue>
                </InfoRow>
              )}
              <InfoRow>
                <InfoLabel>Tipo Cliente</InfoLabel>
                <InfoValue>{formatValue(bill.tipologia === "privato" ? "domestico" : "business")}</InfoValue>
              </InfoRow>
            </Grid>

            {/* CONTRATTO */}
            <Grid item xs={12} md={6}>
              <SectionTitle>CONTRATTO</SectionTitle>
              <InfoRow>
                <InfoLabel>Mercato</InfoLabel>
                <InfoValue>{formatValue(bill.mercato === "libero" ? "libero" : "tutelato")}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Tipo Prezzo</InfoLabel>
                <InfoValue>{formatValue(metadata?.tipoPrezzo)}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Scadenza Offerta</InfoLabel>
                <InfoValue>{formatDate(metadata?.scadenzaOfferta)}</InfoValue>
              </InfoRow>
            </Grid>

            {/* METADATI & ARCHIVIAZIONE */}
            <Grid item xs={12} md={6}>
              <SectionTitle>METADATI & ARCHIVIAZIONE</SectionTitle>
              <InfoRow>
                <InfoLabel>Caricato il</InfoLabel>
                <InfoValue>{formatValue(metadata?.dataCaricamento)}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Versione</InfoLabel>
                <InfoValue>{formatValue(metadata?.versione)}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>ID Utente</InfoLabel>
                <InfoValue>{formatValue(metadata?.idUtente)}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Qualità Riconoscimento</InfoLabel>
                <InfoValue>
                  <Chip 
                    label={metadata?.qualitaRiconoscimento || "n/d"} 
                    color={metadata?.qualitaRiconoscimento === "alta" ? "success" : metadata?.qualitaRiconoscimento === "media" ? "warning" : "error"} 
                    size="small" 
                  />
                </InfoValue>
              </InfoRow>
            </Grid>

            {/* Warning per campi incerti */}
            {campiIncertiList.length > 0 && (
              <Grid item xs={12}>
                <WarningBox>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <WarningAmberIcon sx={{ color: (theme) => theme.palette.mode === "light" ? "#d32f2f" : "#ef9a9a" }} />
                    <Typography variant="subtitle2" fontWeight={700} color={(theme) => theme.palette.mode === "light" ? "#d32f2f" : "#ef9a9a"}>
                      ⚠️ Campi Incerti:
                    </Typography>
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {campiIncertiList.map((campo, index) => (
                      <WarningChip
                        key={index}
                        label={campo}
                        size="small"
                      />
                    ))}
                  </Box>
                </WarningBox>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary">
              Analisi & Comparazione Mercato
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Questa sezione sarà implementata successivamente
            </Typography>
          </Box>
        </TabPanel>
      </DialogContent>

      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "flex-end",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Typography
          variant="button"
          sx={{
            px: 3,
            py: 1,
            backgroundColor: "primary.main",
            color: "white",
            borderRadius: 1,
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "primary.dark",
            },
          }}
          onClick={onClose}
        >
          Chiudi
        </Typography>
      </Box>
    </StyledDialog>
  );
};

export default BillDetailsDialog;
